import { trackCustomAction } from '@openphone/quo-observability'
import type { LogEvent, LogFunction } from 'relay-runtime'

type GroupingOf<Col extends LogEvent, Grp extends string> = Col extends {
  name: `${Grp}.${string}`
}
  ? Col
  : never

interface Options {
  filterEvents?: (logEvent: LogEvent) => boolean
}

const isExecuteEvent = (
  logEvent: LogEvent,
): logEvent is GroupingOf<typeof logEvent, 'execute'> =>
  logEvent.name.startsWith('execute')

const isQueryResourceEvent = (
  logEvent: LogEvent,
): logEvent is GroupingOf<typeof logEvent, 'queryresource'> =>
  logEvent.name.startsWith('queryresource')

const isNetworkEvent = (
  logEvent: LogEvent,
): logEvent is GroupingOf<typeof logEvent, 'network'> =>
  logEvent.name.startsWith('network')

// Performance tracking for discrete relay events
// Duration tracking is handled by createNetworkWithPerformanceTracking
// Never send operation variables: they carry user input (see pii-logging-policy.md)
function createRelayPerformanceTracker() {
  return {
    handleExecuteEvent(logEvent: GroupingOf<LogEvent, 'execute'>) {
      switch (logEvent.name) {
        case 'execute.start':
          if ('executeId' in logEvent) {
            trackCustomAction({
              type: 'relay-execute-start',
              'relay.execute.id': logEvent.executeId,
              'graphql.operation.name': logEvent.params?.name,
              'graphql.operation.type': logEvent.params?.operationKind,
              'relay.cache.id': logEvent.params?.id || undefined,
            })
          }
          break
        case 'execute.next.start':
          trackCustomAction({
            type: 'relay-execute-next-start',
            'relay.execute.id': logEvent.executeId,
            has_data: !!logEvent.response,
            has_error: false,
            'error.count': 0,
          })
          break
        case 'execute.next.end':
          trackCustomAction({
            type: 'relay-execute-next-end',
            'relay.execute.id': logEvent.executeId,
            has_data: !!logEvent.response,
            has_error: false,
            'error.count': 0,
          })
          break
        case 'execute.async.module':
          trackCustomAction({
            type: 'relay-execute-async-module',
            'relay.execute.id': logEvent.executeId,
            'graphql.operation.name': logEvent.operationName,
            duration_ms: logEvent.duration,
          })
          break

        case 'execute.complete':
          if ('executeId' in logEvent) {
            trackCustomAction({
              type: 'relay-execute-complete',
              'relay.execute.id': logEvent.executeId,
              duration_ms:
                'duration' in logEvent ? (logEvent.duration as number) : undefined,
            })
          }
          break

        case 'execute.error':
          if ('executeId' in logEvent) {
            trackCustomAction({
              type: 'relay-execute-error',
              'relay.execute.id': logEvent.executeId,
              error: logEvent.error,
              'exception.type': logEvent.error?.name,
              'exception.message': logEvent.error?.message,
            })
          }
          break

        case 'execute.normalize.start':
          trackCustomAction({
            type: 'relay-normalize-start',
            'relay.execute.id':
              'executeId' in logEvent ? (logEvent.executeId as number) : undefined,
          })
          break

        case 'execute.normalize.end':
          trackCustomAction({
            type: 'relay-normalize-complete',
            'relay.execute.id':
              'executeId' in logEvent ? (logEvent.executeId as number) : undefined,
            duration_ms:
              'duration' in logEvent ? (logEvent.duration as number) : undefined,
          })
          break
      }
    },

    handleNetworkEvent(logEvent: GroupingOf<LogEvent, 'network'>) {
      switch (logEvent.name) {
        case 'network.start':
          if ('params' in logEvent) {
            trackCustomAction({
              type: 'relay-network-request-start',
              'relay.network_request.id': logEvent.networkRequestId,
              'graphql.operation.name': logEvent.params?.name || 'unknown',
              'graphql.operation.type': logEvent.params?.operationKind || 'unknown',
              'relay.cache.id': logEvent.params?.id || undefined,
            })
          }
          break

        case 'network.complete':
          trackCustomAction({
            type: 'relay-network-request-complete',
            'relay.network_request.id': logEvent.networkRequestId,
            duration_ms:
              'duration' in logEvent ? (logEvent.duration as number) : undefined,
          })
          break

        case 'network.error':
          trackCustomAction({
            type: 'relay-network-request-error',
            'relay.network_request.id': logEvent.networkRequestId,
            error: logEvent.error,
            'exception.type': logEvent.error?.name,
            'exception.message': logEvent.error?.message,
          })
          break

        case 'network.info':
          trackCustomAction({
            type: 'relay-network-info',
            'relay.network_request.id': logEvent.networkRequestId,
            'relay.network.info': 'info' in logEvent ? logEvent.info : undefined,
          })
          break
      }
    },

    handleQueryResourceEvent(logEvent: GroupingOf<LogEvent, 'queryresource'>) {
      switch (logEvent.name) {
        case 'queryresource.fetch':
          if ('operation' in logEvent) {
            trackCustomAction({
              type: 'relay-queryresource-fetch',
              'relay.resource.id': logEvent.resourceID,
              'graphql.operation.name':
                logEvent.operation.request.node.params?.name || 'unknown',
              'graphql.operation.type':
                logEvent.operation.request.node.params?.operationKind,
              'relay.cache.id': logEvent.operation.request.node.params?.id ?? undefined,
              'relay.should_fetch': logEvent.shouldFetch ? 'true' : 'false',
              'relay.fetch_policy': logEvent.fetchPolicy,
              'relay.render_policy': logEvent.renderPolicy,
              'relay.query_availability':
                'queryAvailability' in logEvent ? logEvent.queryAvailability : undefined,
            })
          }
          break
      }
    },
  }
}

/**
 * Enhanced Relay log function for DataDog performance metrics
 */
export default function createRelayPerformanceLogFunction({
  filterEvents,
}: Options = {}): LogFunction {
  const relayTracker = createRelayPerformanceTracker()

  return (logEvent) => {
    if (typeof filterEvents === 'function' && !filterEvents(logEvent)) {
      return
    }

    // Handle DataDog performance metrics
    if (isExecuteEvent(logEvent)) {
      relayTracker.handleExecuteEvent(logEvent)
    } else if (isQueryResourceEvent(logEvent)) {
      relayTracker.handleQueryResourceEvent(logEvent)
    } else if (isNetworkEvent(logEvent)) {
      relayTracker.handleNetworkEvent(logEvent)
    }
  }
}
